import React from "react";
import { Typography, Box, Chip } from "@mui/material";
import {
  QuestionSidebarWrapper,
  QuestionNavButton,
  QuestionPanelWrapper,
} from "./style";

const getDifficultyColor = (difficulty) => {
  switch (difficulty?.toLowerCase()) {
    case "easy":
      return { bg: "#e8f5e9", color: "#2e7d32" };
    case "medium":
      return { bg: "#fff3e0", color: "#e65100" };
    case "hard":
      return { bg: "#ffebee", color: "#c62828" };
    default:
      return { bg: "#f5f5f5", color: "#616161" };
  }
};

const formatSampleValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};

const QuestionSidebar = ({
  questions,
  currentQuestion,
  savedQuestions,
  onQuestionClick,
}) => (
  <QuestionSidebarWrapper>
    {questions.map((_, idx) => (
      <QuestionNavButton
        key={idx}
        active={currentQuestion === idx ? 1 : 0}
        saved={savedQuestions[idx] ? 1 : 0}
        onClick={() => onQuestionClick(idx)}
      >
        Q{idx + 1}
      </QuestionNavButton>
    ))}
  </QuestionSidebarWrapper>
);

const QuestionPanel = ({ question, questionNumber }) => {
  if (!question) return null;

  const diffColor = getDifficultyColor(question.difficulty);

  return (
    <QuestionPanelWrapper>
      <Box className="question-header">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "14px", color: "#667085", fontWeight: 600 }}>
            Question {questionNumber}
          </Typography>
          <Chip
            label={question.difficulty}
            size="small"
            sx={{
              backgroundColor: diffColor.bg,
              color: diffColor.color,
              fontWeight: 700,
              fontSize: "12px",
              height: "24px",
            }}
          />
        </Box>
        <Typography className="question-title">{question.title}</Typography>
      </Box>

      <Box className="question-body">
        <Typography sx={{ whiteSpace: "pre-wrap", fontSize: "14px", lineHeight: 1.7 }}>
          {question.question_prompt}
        </Typography>

        {question.sample_dataset && (
          <Box sx={{ mt: 2 }}>
            {question.sample_dataset.input !== undefined && (
              <Box className="sample-section">
                <Typography className="sample-label">Input</Typography>
                <Typography className="sample-value">
                  {formatSampleValue(question.sample_dataset.input)}
                </Typography>
              </Box>
            )}

            {question.sample_dataset.output !== undefined && (
              <Box className="sample-section">
                <Typography className="sample-label">Output</Typography>
                <Typography className="sample-value">
                  {formatSampleValue(question.sample_dataset.output)}
                </Typography>
              </Box>
            )}

            {question.sample_dataset.explanation && (
              <Box className="sample-section">
                <Typography className="sample-label">Explanation</Typography>
                <Typography className="sample-value">
                  {question.sample_dataset.explanation}
                </Typography>
              </Box>
            )}

            {question.sample_dataset.datasets?.length > 0 &&
              question.sample_dataset.datasets.map((ds, i) => (
                <Box className="sample-section" key={i}>
                  <Typography className="sample-label">
                    Dataset: {ds.name || `Dataset ${i + 1}`}
                  </Typography>
                  <Typography className="sample-value">
                    {formatSampleValue(ds.rows)}
                  </Typography>
                </Box>
              ))}
          </Box>
        )}
      </Box>
    </QuestionPanelWrapper>
  );
};

const AssessmentQuestionSection = ({
  questions,
  currentQuestion,
  savedQuestions,
  onQuestionClick,
}) => {
  const question = questions[currentQuestion];

  return (
    <>
      <QuestionSidebar
        questions={questions}
        currentQuestion={currentQuestion}
        savedQuestions={savedQuestions}
        onQuestionClick={onQuestionClick}
      />
      <QuestionPanel question={question} questionNumber={currentQuestion + 1} />
    </>
  );
};

export default AssessmentQuestionSection;
